import React from "react";
import EditableText from "./EditableText";
import { useContent } from "../context/ContentContext";

export default function Footer() {
  const { content } = useContent();
  const footer = content?.footer;
  if (!footer) return null;

  const links = footer.nav_links ?? [];

  const scrollTo = (anchor) => (e) => {
    if (!anchor?.startsWith("#")) return;
    e.preventDefault();
    const id = anchor.slice(1);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <footer
      className="relative bg-[#00011F] text-[#F9F8F6] overflow-hidden"
      data-testid="site-footer"
    >
      {/* Giant faded logo watermark sitting behind everything */}
      <img
        src="/alpha-trust-logo.png"
        alt=""
        aria-hidden
        className="pointer-events-none select-none absolute left-1/2 -translate-x-1/2 bottom-10 md:bottom-14 w-[135%] md:w-[110%] max-w-none opacity-[0.07]"
        draggable={false}
      />

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 pt-4 md:pt-6 pb-6 md:pb-8">
        {/* Nav link index */}
        {links.length > 0 && (
          <nav className="grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-3 max-w-xl">
            {links.map((l, i) => (
              <a
                key={l.number}
                href={l.anchor}
                onClick={scrollTo(l.anchor)}
                className="group md:col-span-12 flex items-baseline justify-between border-b border-[#F9F8F6]/15 py-3 md:py-3.5 hover:border-[#F9F8F6]/50 transition-colors"
                data-testid={`footer-nav-${i + 1}`}
              >
                <div className="flex items-baseline gap-3">
                  <EditableText
                    path={`footer.nav_links.${i}.label`}
                    value={l.label}
                    as="span"
                    className="font-serif text-[#F9F8F6] text-lg md:text-xl font-semibold group-hover:translate-x-1 transition-transform"
                    testId={`footer-nav-${i + 1}-label`}
                  />
                  <svg
                    aria-hidden
                    viewBox="0 0 16 16"
                    className="w-3 h-3 text-[#F9F8F6]/70 group-hover:text-[#F9F8F6] transition-colors mt-0.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <path d="M3 13L13 3M13 3H5M13 3V11" />
                  </svg>
                </div>
                <EditableText
                  path={`footer.nav_links.${i}.number`}
                  value={l.number}
                  as="span"
                  className="font-sans text-[#F9F8F6]/55 text-[10px] tracking-[0.2em] tabular-nums"
                  testId={`footer-nav-${i + 1}-number`}
                />
              </a>
            ))}
          </nav>
        )}

        {/* Spacer so the watermark has room */}
        <div className="h-20 md:h-32" aria-hidden />

        {/* Bottom bar */}
        <div className="border-t border-[#F9F8F6]/20 pt-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 md:gap-6">
          <EditableText
            path="footer.copyright"
            value={footer.copyright}
            as="div"
            className="text-[#F9F8F6]/70 text-[11px] md:text-xs tracking-wide"
            testId="footer-copyright"
          />
          {footer.designed_by && (
            <EditableText
              path="footer.designed_by"
              value={footer.designed_by}
              as="div"
              className="text-[#F9F8F6]/60 text-[11px] md:text-xs tracking-wide"
              testId="footer-designed-by"
            />
          )}
        </div>

        {footer.legal && (
          <div className="mt-4 pt-4 border-t border-[#F9F8F6]/10">
            <EditableText
              path="footer.legal"
              value={footer.legal}
              as="p"
              multiline
              className="text-[#F9F8F6]/55 text-[10px] md:text-[11px] leading-[1.6] max-w-5xl"
              testId="footer-legal"
            />
          </div>
        )}
      </div>
    </footer>
  );
}
